import React, { useState, useEffect } from 'react';
import { Grid3X3, Settings, Users, Eye, Home, Shield, Database, FileText, BarChart3, Calendar, Mail, Bell, Lock, Key, CreditCard, Package, Truck, MapPin, Phone, Building, UserCheck, UserX, Folder, File, Search, Filter, Download, Upload, Edit, Trash2, Plus, Minus } from 'lucide-react';
import { type Module } from '../../services/security/modules.service';
import { ConfirmationToast } from '../ui/ConfirmationToast';
import { NotificationToast } from '../ui/NotificationToast';

interface ModuleDetailsDrawerProps {
  isOpen: boolean;
  module: Module | null;
  onClose: () => void;
  onEditModule?: (module: Module) => void;
  onToggleModuleStatus?: (module: Module) => Promise<void>;
  onDeleteModule?: (module: Module) => void;
}

export const ModuleDetailsDrawer: React.FC<ModuleDetailsDrawerProps> = ({
  isOpen,
  module,
  onClose,
  onEditModule,
  onToggleModuleStatus,
  onDeleteModule
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentModule, setCurrentModule] = useState<Module | null>(null);
  const [activeTab, setActiveTab] = useState<'info'>('info');
  
  // Estados para confirmación y loading
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<'toggle' | 'delete' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);


  useEffect(() => {
    if (isOpen && module) {
      setCurrentModule(module);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen, module]);

  useEffect(() => {
    if (!isVisible && !isOpen) {
      const timer = setTimeout(() => {
        setCurrentModule(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (activo: boolean) => {
    return activo 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const getTypeText = (tipo: string) => {
    return tipo === 'navigation' ? 'Navegación' : 'Modal';
  };

  const getTypeColor = (tipo: string) => {
    return tipo === 'navigation'
      ? 'bg-blue-100 text-blue-800'
      : 'bg-purple-100 text-purple-800';
  };

  const handleEditModule = () => {
    if (currentModule && onEditModule) {
      onEditModule(currentModule);
    }
  };

  const handleToggleModuleStatus = () => {
    setConfirmationAction('toggle');
    setShowConfirmation(true);
  };

  const handleDeleteModule = () => {
    setConfirmationAction('delete');
    setShowConfirmation(true);
  };

  const handleConfirmAction = async () => {
    if (!currentModule || !confirmationAction) return;
    
    setIsLoading(true);
    try {
      if (confirmationAction === 'toggle' && onToggleModuleStatus) {
        await onToggleModuleStatus(currentModule);
        
        setNotificationConfig({
          title: '¡Éxito!',
          message: `Módulo ${currentModule.activo ? 'desactivado' : 'activado'} correctamente`,
          type: 'success'
        });
        setShowNotification(true);
      } else if (confirmationAction === 'delete' && onDeleteModule) {
        onDeleteModule(currentModule);
        handleClose();
        return;
      }
    } catch (error) {
      console.error('Error al ejecutar acción:', error);
      
      setNotificationConfig({
        title: 'Error',
        message: 'No se pudo completar la operación. Inténtalo nuevamente.',
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
      setConfirmationAction(null);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  const getConfirmationConfig = () => {
    if (!currentModule || !confirmationAction) return null;
    
    if (confirmationAction === 'toggle') {
      return {
        title: currentModule.activo ? 'Desactivar Módulo' : 'Activar Módulo',
        message: currentModule.activo 
          ? `¿Estás seguro de que deseas desactivar el módulo "${currentModule.nombre}"? Este módulo dejará de estar disponible para asignar a roles.`
          : `¿Estás seguro de que deseas activar el módulo "${currentModule.nombre}"? Este módulo estará disponible para asignar a roles.`,
        confirmText: currentModule.activo ? 'Desactivar' : 'Activar',
        type: currentModule.activo ? 'danger' as const : 'success' as const
      };
    }

    if (confirmationAction === 'delete') {
      return {
        title: 'Eliminar Módulo',
        message: `¿Estás seguro de que deseas eliminar el módulo "${currentModule.nombre}"? Esta acción no se puede deshacer y el módulo será removido de todos los roles que lo tengan asignado.`,
        confirmText: 'Eliminar',
        type: 'danger' as const
      };
    }
    
    return null;
  };

  const getModuleIcon = (iconName: string) => {
    const icons: { [key: string]: React.ComponentType<any> } = {
      'settings': Settings,
      'users': Users,
      'grid3x3': Grid3X3,
      'eye': Eye,
      'home': Home,
      'shield': Shield,
      'database': Database,
      'filetext': FileText,
      'barchart3': BarChart3,
      'calendar': Calendar,
      'mail': Mail,
      'bell': Bell,
      'lock': Lock,
      'key': Key,
      'creditcard': CreditCard,
      'package': Package,
      'truck': Truck,
      'mappin': MapPin,
      'phone': Phone,
      'building': Building,
      'usercheck': UserCheck,
      'userx': UserX,
      'folder': Folder,
      'file': File,
      'search': Search,
      'filter': Filter,
      'download': Download,
      'upload': Upload,
      'edit': Edit,
      'trash2': Trash2,
      'plus': Plus,
      'minus': Minus
    };
    
    return icons[iconName] || Grid3X3;
  };


  if (!isOpen && !currentModule) return null;
  if (!currentModule) return null;

  const IconComponent = getModuleIcon(currentModule.icono || 'grid3x3');

  return (
    <div className="fixed left-0 right-0 bottom-0 top-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        <section className={`absolute right-0 top-0 h-full w-full max-w-4xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-medium text-gray-900">
                  Detalles del Módulo
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Header del módulo */}
              <div className="text-center py-6 bg-gradient-to-b from-gray-50 to-white">
                <div className="mx-auto h-20 w-20 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center mb-3 shadow-lg">
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xs font-medium text-gray-900 mb-1">
                  {currentModule.nombre}
                </h3>
                {currentModule.descripcion && (
                  <p className="text-gray-600 mb-2">{currentModule.descripcion}</p>
                )}
                <div className="flex justify-center space-x-2">
                  <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(currentModule.activo)}`}>
                    {currentModule.activo ? 'Activo' : 'Inactivo'}
                  </span>
                  <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getTypeColor(currentModule.tipo)}`}>
                    {getTypeText(currentModule.tipo)}
                  </span>
                </div>
              </div>

              {/* Contenido */}
              <div className="px-6 py-6">
                  <div>
                    {/* Información básica */}
                    <div className="mb-6">
                      <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                        Información Básica
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Nombre del Módulo</label>
                          <p className="text-gray-900 font-medium">{currentModule.nombre}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Tipo</label>
                          <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${getTypeColor(currentModule.tipo)}`}>
                            {getTypeText(currentModule.tipo)}
                          </span>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Estado</label>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${currentModule.activo ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <p className="text-gray-900 font-medium">{currentModule.activo ? 'Activo' : 'Inactivo'}</p>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Orden</label>
                          <p className="text-gray-900 font-medium">{currentModule.orden}</p>
                        </div>
                        {currentModule.ruta && (
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-500">Ruta</label>
                            <p className="text-gray-900 font-mono text-xs bg-gray-100 px-2 py-1 rounded">{currentModule.ruta}</p>
                          </div>
                        )}
                        {currentModule.descripcion && (
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-500">Descripción</label>
                            <p className="text-gray-900">{currentModule.descripcion}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Información del Sistema */}
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="text-xs font-medium text-gray-600 mb-3">
                        Información del Sistema
                      </h4>
                      <div className="space-y-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                        <div><span className="font-medium">ID:</span> {currentModule.id}</div>
                        <div><span className="font-medium">Creado:</span> {formatDate(currentModule.creadoEn)}</div>
                        <div><span className="font-medium">Última actualización:</span> {formatDate(currentModule.actualizadoEn)}</div>
                        {currentModule.moduloPadreId && (
                          <div><span className="font-medium">Módulo padre ID:</span> {currentModule.moduloPadreId}</div>
                        )}
                      </div>
                    </div>
                  </div>
              </div>
            </div>

            {/* Footer con acciones */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex space-x-3">
                {onEditModule && (
                  <button 
                    onClick={handleEditModule}
                    className="flex-1 bg-blue-600 text-white px-3 py-2 text-xs rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Editar Módulo
                  </button>
                )}
                {onToggleModuleStatus && (
                  <button 
                    onClick={handleToggleModuleStatus}
                    disabled={isLoading}
                    className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                      currentModule.activo 
                        ? 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100' 
                        : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                    }`}
                  >
                    {currentModule.activo ? 'Desactivar' : 'Activar'}
                  </button>
                )}
                {onDeleteModule && (
                  <button 
                    onClick={handleDeleteModule}
                    className="flex-1 bg-red-50 text-red-700 border border-red-200 px-3 py-2 text-xs rounded-lg hover:bg-red-100 transition-colors font-medium"
                  >
                    Eliminar
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Confirmation Toast */}
      {(() => {
        const config = getConfirmationConfig();
        return config ? (
          <ConfirmationToast
            isOpen={showConfirmation}
            title={config.title}
            message={config.message}
            confirmText={config.confirmText}
            type={config.type}
            onConfirm={handleConfirmAction}
            onCancel={handleCancelAction}
            isLoading={isLoading}
          />
        ) : null;
      })()}
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default ModuleDetailsDrawer;